import { ApolloClient } from '@apollo/client';
import { createStore } from 'zustand';

import {
  GetReviewsQuery,
  GetReviewsQueryVariables,
  ReviewOrderBy,
} from '@notino/shared/definitions/types';

import getReviewsQuery from '@queries/useReviews/queries/reviews.graphql';

export type PhotoWithReview = NonNullable<
  NonNullable<
    NonNullable<GetReviewsQuery['reviewsAndFilters']>['data']
  >['reviews'][0]['images']
>[0] & {
  reviewId: number;
};

type PhotosStore = {
  photos: PhotoWithReview[];
  hasMore: boolean;
  page: number;
  selectedPhoto?: PhotoWithReview;
  actions: {
    fetchMorePhotos: () => Promise<void>;
    setSelectedPhoto: (photo: PhotoWithReview | undefined) => void;
  };
};

export type PhotosStoreStore = ReturnType<typeof createPhotosStore>;

export const createPhotosStore = ({
  initialPhotos,
  hasMore,
  masterProductId,
  apollo,
}: {
  apollo: ApolloClient<object>;
  masterProductId: string;
  hasMore: boolean;
  initialPhotos: PhotoWithReview[];
}) =>
  createStore<PhotosStore>((set, get) => ({
    photos: initialPhotos,
    hasMore,
    page: 1,
    actions: {
      fetchMorePhotos: async () => {
        const pageToFetch = get().page + 1;
        const res = await apollo.query<
          GetReviewsQuery,
          GetReviewsQueryVariables
        >({
          query: getReviewsQuery,
          variables: {
            masterProductId,
            orderBy: ReviewOrderBy.DateTime,
            orderDesc: true,
            page: pageToFetch,
            contentTypes: ['WithImages'],
          },
          fetchPolicy: 'network-only',
        });

        set({
          page: pageToFetch,
          photos: mapPhotos(res.data),
          hasMore:
            (res.data?.reviewsAndFilters?.data?.totalPages ?? 0) > pageToFetch,
        });
      },

      setSelectedPhoto: (photo) => set({ selectedPhoto: photo }),
    },
  }));

export const mapPhotos = (data: GetReviewsQuery | undefined) => {
  return (
    data?.reviewsAndFilters?.data?.reviews.flatMap((review) =>
      (review.images ?? []).map((i) => ({ ...i, reviewId: review.id }))
    ) ?? []
  );
};
